import React, { useEffect, useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import * as api from 'config/api';
import * as consts from 'config/consts';
import { getDate } from 'components/features/utils/converter';
import { formatDate } from 'utils/formatter';
import { PeriodType } from 'pages/AssetTransition';
import Icon from 'components/commons/icon/Icon';
import NumberLabel from 'components/features/utils/NumberLabel';
import NothingAssetNoDataMessageView from 'components/features/NothingAssetNoDataMessageView';
import {
  CalculateVAxisType,
  calclateAssrtControl,
  calculateVAxisForAssetAllocation,
  getAmount,
  maxTotalAssetAmount,
} from '../utils/chart';
import HighchartsReact from 'highcharts-react-official';
import * as Highcharts from 'highcharts';
import { unitFormat } from '../new-report/utils/constructor';
import { AreaChartLegend, LineChartLegend, marker } from '../utils/chartUtils';
import {
  ExtraTimeSeriesData,
  TimeSeriesDatum,
} from '../home/assetStuffing/AssetStuffing';
import { IconOnChart } from '../home/assetStuffing/AssetStuffingChart';
import DateButton from './DateButton';

type AssetAllocationChartProps = {
  assetList: Array<api.GetAssetResultItem> | undefined;
  changeTotalAssetAmount: number | undefined;
  dateSelectIndex: number;
  limitPeriod: PeriodType | undefined;
  targetPeriod: PeriodType | undefined;
  chagePeriod: (selectIndex: number, shift: number) => void;
  today: number;
};

const AssetAllocationChart: React.FC<AssetAllocationChartProps> = ({
  assetList,
  changeTotalAssetAmount,
  dateSelectIndex,
  limitPeriod,
  targetPeriod,
  chagePeriod,
  today,
}) => {
  const assrtControl = calclateAssrtControl(maxTotalAssetAmount(assetList));
  const targetPeriodView =
    dateSelectIndex === consts.ChartPeriodTypes.直近 ? (
      <div className="flex-grow">
        <div className="flex justify-center">
          <div className="text-2xl">直近14日</div>
        </div>
      </div>
    ) : dateSelectIndex === consts.ChartPeriodTypes.週 ? (
      <div className="flex-grow">
        <div className="flex justify-center">
          <div className="pt-2">{formatDate(targetPeriod?.From, 'M')}月</div>
          <div className="pl-1 text-2xl">
            {formatDate(targetPeriod?.From, 'd')}
          </div>
          <div className="pt-2">日～</div>
          <div className="pt-2">{formatDate(targetPeriod?.To, 'M')}月</div>
          <div className="pl-1 text-2xl">
            {formatDate(targetPeriod?.To, 'd')}
          </div>
          <div className="pt-2">日</div>
        </div>
      </div>
    ) : dateSelectIndex === consts.ChartPeriodTypes.月 ? (
      <div className="flex-grow">
        <div className="flex justify-center">
          <div className="pt-2">{formatDate(targetPeriod?.From, 'yyyy')}年</div>
          <div className="pl-1 text-2xl">
            {formatDate(targetPeriod?.From, 'M')}
          </div>
          <div className="pt-2">月</div>
        </div>
      </div>
    ) : dateSelectIndex === consts.ChartPeriodTypes.年 ? (
      <div className="flex-grow">
        <div className="flex justify-center">
          <div className="text-2xl">
            {formatDate(targetPeriod?.From, 'yyyy')}
          </div>
          <div className="pt-2">年</div>
        </div>
      </div>
    ) : null;

  const textPeriod =
    dateSelectIndex === consts.ChartPeriodTypes.直近
      ? '直近'
      : dateSelectIndex === consts.ChartPeriodTypes.週
      ? '今週'
      : dateSelectIndex === consts.ChartPeriodTypes.月
      ? `${formatDate(targetPeriod?.From, 'M')}月`
      : dateSelectIndex === consts.ChartPeriodTypes.年
      ? `${formatDate(targetPeriod?.From, 'yyyy')}年`
      : '';

  const isShowPrev =
    limitPeriod && targetPeriod && limitPeriod?.From < targetPeriod?.From;
  function onPrev() {
    chagePeriod(dateSelectIndex, -1);
  }
  const prevBtnView = isShowPrev ? (
    <div onClick={onPrev}>
      <Icon
        className="text-4xl text-gray-500 cursor-pointer"
        name="chevron-left"
      />
    </div>
  ) : (
    <div style={{ height: '36px', width: '36px' }} />
  );

  const isShowNext =
    limitPeriod && targetPeriod && limitPeriod?.To > targetPeriod?.To;
  function onNext() {
    chagePeriod(dateSelectIndex, 1);
  }
  const nextBtnView = isShowNext ? (
    <div onClick={onNext}>
      <Icon
        className="text-4xl text-gray-500 cursor-pointer"
        name="chevron-right"
      />
    </div>
  ) : (
    <div style={{ height: '36px', width: '36px' }} />
  );

  const isShowChart = assetList && assetList?.length > 0 ? '' : 'hidden';

  const extraData = useMemo(() => {
    if (assetList === undefined) return;
    const incorrectSign: ExtraTimeSeriesData = {
      type: 'IncorrectSign',
      value: assetList?.map(it => {
        return {
          value: it.Incorrect,
          date: getDate(it.Day),
        } as TimeSeriesDatum;
      }),
    };
    return [incorrectSign];
  }, [assetList]);

  return (
    <div>
      {/* 年月週ボタン */}
      <DateButton
        selectIndex={dateSelectIndex}
        omitTypes={[consts.ChartPeriodTypes.日]}
        onChangeDate={(selectIndex: number) => {
          chagePeriod(selectIndex, 0);
        }}
      />

      {/* 期間表示 */}
      <div className="flex font-bold px-2">
        {prevBtnView}
        {targetPeriodView}
        {nextBtnView}
      </div>
      <div className="flex border-b border-solid border-gray-300" />

      {/* 資産変動 */}
      <div className="mx-2 mt-3">
        <div className="px-4 flex font-bold">
          <div className="text-gray-500 p-1">{textPeriod}の資産変動</div>
          <NumberLabel
            className="flex-grow flex justify-end ml-2 text-xl"
            value={changeTotalAssetAmount}
            unitName="円"
          />
        </div>

        {/* チャートデータがない表示 */}
        <NothingAssetNoDataMessageView assetList={assetList} />

        {/* チャート */}
        <div className={`${isShowChart}`}>
          <HighChartWrapper
            assetList={assetList}
            dateSelectIndex={dateSelectIndex}
            getAmount={v =>
              getAmount(v, assrtControl.unit === 1 ? 10000 : assrtControl.unit)
            }
            today={today}
            assrtControlPattern={assrtControl.pattern}
            calculatedVAxis={calculateVAxisForAssetAllocation(
              assetList,
              assrtControl.unit
            )}
            extraData={extraData}
          />
        </div>
      </div>
    </div>
  );
};

type ChartWrapperProps = {
  dateSelectIndex: number;
  assetList?: api.GetAssetResultItem[];
  getAmount: (value: number | null) => number | null;
  today: number;
  assrtControlPattern: string;
  calculatedVAxis: CalculateVAxisType;
  extraData?: ExtraTimeSeriesData[];
};

const getLastDay = (d: Date) => {
  const y = d.getMonth() + 1 === 12 ? d.getFullYear() + 1 : d.getFullYear();
  const ret = new Date(
    new Date(y, (d.getMonth() + 1) % 12, 1).getTime() - 24 * 60 * 60 * 1000
  );
  return ret;
};

const HighChartWrapper = ({
  dateSelectIndex,
  assetList,
  getAmount,
  today,
  assrtControlPattern,
  calculatedVAxis: calculatedYAxis,
  extraData,
}: ChartWrapperProps) => {
  const M_ = (d: Date): string => {
    return `${d.getMonth() + 1}`;
  };
  const d_ = (d: Date): string => {
    return `${d.getDate()}`;
  };
  const day_ = (d: Date): string => {
    return `${['日', '月', '火', '水', '木', '金', '土'][d.getDay()]}`;
  };

  const getFormatter = (
    chartPeriodType: typeof consts.ChartPeriodTypes[keyof typeof consts.ChartPeriodTypes],
    isSetNewLine: boolean
  ) => (date: Date): string => {
    const Md = (d: Date): string => {
      return `${M_(d)}/${d_(d)}`;
    };
    switch (chartPeriodType) {
      case consts.ChartPeriodTypes.直近:
        return Md(date);
      case consts.ChartPeriodTypes.週:
        return `${Md(date)}<br/>${day_(date)}`;
      case consts.ChartPeriodTypes.月:
        return Md(date);
    }
    return `${M_(date)}${isSetNewLine ? '<br/>' : ''}月`;
  };
  const isSetNewLine = window.innerWidth < 768;
  const formatter = getFormatter(
    dateSelectIndex as typeof consts.ChartPeriodTypes[keyof typeof consts.ChartPeriodTypes],
    isSetNewLine
  );

  const colors: { [k: string]: string } = {
    国内株式: '#179a8d',
    未上場株式: '#8C8C8C',
    投資信託: '#dd80d4',
    外国株式: '#3fa9f5',
    '国内ETF・ETN': '#6D71DA',
    '外国ETF・ETN': '#17CECE',
    国内REIT: '#FE5F5F',
    外国REIT: '#D09494',
    国内債券: '#26E016',
    外国債券: '#F0C22B',
    国内EB: '#4098B4',
    外国EB: '#91BEAB',
    不動産: '#FF58DA',
    ロボアド: '#f47f37',
    株式信用: '#7ad8c6',
    '預金・MRF': '#9f3bf4',
    その他: '#d2deef',
  };

  const list = (
    assetList
      ?.map(x => {
        return {
          date: getDate(x.Day).getTime(),
          stock: getAmount(x.StockAmount),
          unlistedStock: getAmount(x.UnlistedStockAmount),
          investmentTrustAmount: getAmount(x.InvestmentTrustAmount),
          foreignStock: getAmount(x.USStockAmount),
          etfEtn: getAmount(x.EtfEtnAmount),
          foreignEtfEtn: getAmount(x.ForeignEtfEtnAmount),
          reit: getAmount(x.ReitAmount),
          foreignReit: getAmount(x.ForeignReitAmount),
          bond: getAmount(x.BondAmount),
          foreignBond: getAmount(x.ForeignBondAmount),
          eb: getAmount(x.EbAmount),
          foreignEb: getAmount(x.ForeignEbAmount),
          realEstate: getAmount(x.RealEstateAmount),
          roboAdvisor: getAmount(x.RoboAdvisorAmount),
          margin: getAmount(x.MarginAmount),
          cash: getAmount(x.CashAmount),
          other: getAmount(x.OtherAmount),
        };
      })
      .sort((a, b) => a.date - b.date) ?? []
  ).reduce(
    (acc, v) => {
      const date =
        dateSelectIndex !== consts.ChartPeriodTypes.年
          ? v.date
          : getLastDay(new Date(v.date)).getTime();
      acc.date.push(date);
      acc.values.国内株式.push(name => ({ name, x: date, y: v.stock }));
      acc.values.未上場株式.push(name => ({
        name,
        x: date,
        y: v.unlistedStock,
      }));
      acc.values.投資信託.push(name => ({
        name,
        x: date,
        y: v.investmentTrustAmount,
      }));
      acc.values.外国株式.push(name => ({ name, x: date, y: v.foreignStock }));
      acc.values['国内ETF・ETN'].push(name => ({ name, x: date, y: v.etfEtn }));
      acc.values['外国ETF・ETN'].push(name => ({
        name,
        x: date,
        y: v.foreignEtfEtn,
      }));
      acc.values.国内REIT.push(name => ({ name, x: date, y: v.reit }));
      acc.values.外国REIT.push(name => ({ name, x: date, y: v.foreignReit }));
      acc.values.国内債券.push(name => ({ name, x: date, y: v.bond }));
      acc.values.外国債券.push(name => ({ name, x: date, y: v.foreignBond }));
      acc.values.国内EB.push(name => ({ name, x: date, y: v.eb }));
      acc.values.外国EB.push(name => ({ name, x: date, y: v.foreignEb }));
      acc.values.不動産.push(name => ({ name, x: date, y: v.realEstate }));
      acc.values.ロボアド.push(name => ({ name, x: date, y: v.roboAdvisor }));
      acc.values.株式信用.push(name => ({ name, x: date, y: v.margin }));
      acc.values['預金・MRF'].push(name => ({ name, x: date, y: v.cash }));
      acc.values.その他.push(name => ({ name, x: date, y: v.other }));
      return acc;
    },
    {
      date: [] as number[],
      values: {
        国内株式: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        未上場株式: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        投資信託: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        外国株式: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        '国内ETF・ETN': [] as ((
          name: string
        ) => Highcharts.PointOptionsObject)[],
        '外国ETF・ETN': [] as ((
          name: string
        ) => Highcharts.PointOptionsObject)[],
        国内REIT: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        外国REIT: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        国内債券: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        外国債券: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        国内EB: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        外国EB: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        不動産: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        ロボアド: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        株式信用: [] as ((name: string) => Highcharts.PointOptionsObject)[],
        '預金・MRF': [] as ((name: string) => Highcharts.PointOptionsObject)[],
        その他: [] as ((name: string) => Highcharts.PointOptionsObject)[],
      },
    }
  );

  const series: Highcharts.SeriesOptionsType[] = Object.entries(
    list.values
  ).flatMap(e => {
    const [k, v] = e;
    const ret: Highcharts.SeriesOptionsType = {
      type: 'area',
      name: k,
      color: colors[k] ?? '',
      lineColor: colors[k] ?? '',
      data: v.map(f => f(k)),
      marker: {
        enabled: false,
        height: 8,
        width: 8,
        ...marker.marker,
      },
      fillOpacity: 0.6,
    };
    if (v.every(f => f(k).y === null)) return [];
    return [ret];
  });

  const [icons, setIcons] = useState<IconOnChart[]>([]);
  const [toolTipPosition, setToolTipPosition] = useState<{
    x: number;
    y: number;
    width: number;
    date: Date | null;
  }>({ x: 0, y: 0, width: 0, date: null });
  const [showToolTip, setShowToolTip] = useState<boolean>(false);

  const [options, setOptions] = useState<Highcharts.Options>({
    credits: { enabled: false },
    legend: {
      align: 'left',
      enabled: true,
      verticalAlign: 'top',
      useHTML: true,
      itemDistance: -10,
      margin: 15,
      itemStyle: { fontSize: '10px', fontWeight: 'normal' },
      maxHeight: 52,
      itemWidth: 100,
      navigation: {
        activeColor: '#fdba74',
        arrowSize: 12,
        style: { x: 20 },
      },
      className: 'override-highcharts-legend common-margin-top',
      labelFormatter: function() {
        if (this instanceof Highcharts.Series) {
          const name = this.name;
          const color = (() => {
            const t = this as unknown;
            if (
              typeof t === 'object' &&
              t &&
              'color' in t &&
              typeof (t as { color: unknown }).color === 'string'
            ) {
              return (t as { color: string }).color;
            }
            return '';
          })();
          if (this.type === 'area') {
            return renderToStaticMarkup(
              <AreaChartLegend color={color} lightOpacity={0.6}>
                {name}
              </AreaChartLegend>
            );
          }
          if (this.type === 'line') {
            return renderToStaticMarkup(
              <LineChartLegend color={color}>{name}</LineChartLegend>
            );
          }
        }
        return '';
      },
    },
    title: {
      text: '',
    },
    subtitle: {
      text: '',
    },
    plotOptions: {
      series: { ...marker, turboThreshold: 15000 },
      area: {
        stacking: 'normal',
        lineWidth: 1,
        turboThreshold: 15000,
      },
    },
    tooltip: {
      useHTML: true,
      shared: true,
      borderColor: 'transparent',
    },
    chart: {
      marginLeft: 0,
      height: 220,
      events: {
        redraw: function() {
          if (ref.current) {
            const chart = ref.current.chart;
            drawIcons({ chart, icons });
          }
        },
        render: function() {
          return icons.forEach(({ icon }) => {
            if (icon.element) icon.toFront();
          });
        },
      },
    },
    xAxis: {
      labels: { style: { fontSize: '9px' } },
      crosshair: { snap: true, color: '#8e9db2', width: 2 },
    },
    yAxis: {
      opposite: true,
      title: {
        text: '(万円)',
        align: 'high',
        offset: 0,
        rotation: 0,
        x: 10,
        y: -15,
      },
    },
    exporting: {
      enabled: false,
    },
    series,
  });

  useEffect(() => {
    setOptions(prev => {
      return {
        ...prev,
        tooltip: {
          outside: true,
          ...prev.tooltip,
          formatter: function() {
            const Tooltip: React.FC<{
              name: string;
              color: string;
              value: number;
            }> = ({ name, color, value }) => {
              return (
                <div className={`tooltip`}>
                  <div
                    className="tooltip-point"
                    style={{ backgroundColor: color }}
                  />
                  <span className="tooltip-label">{name}:</span>
                  <span className="tooltip-value">
                    {unitFormat(value, 2)}万円
                  </span>
                </div>
              );
            };
            return renderToStaticMarkup(
              <>
                <span className="tooltip-header">
                  {formatter(new Date(this.x)).replace('<br/>', ' ')}
                </span>
                {this.points?.map(x => {
                  return (
                    <Tooltip
                      name={x.point.name}
                      color={x.color?.toString() ?? ''}
                      value={x.y ?? 0}
                    />
                  );
                })}
              </>
            );
          },
        },
        xAxis: {
          ...prev.xAxis,
          labels: {
            ...(prev.xAxis instanceof Array ? prev.xAxis[0] : prev.xAxis)
              ?.labels,
            formatter: function() {
              return formatter(new Date(this.value));
            },
          },
          tickPositions: list.date
            .map(d => {
              if (dateSelectIndex !== consts.ChartPeriodTypes.年) return d;
              const date = new Date(d);
              return getLastDay(date).getTime();
            })
            .filter((_, i) => {
              return (
                dateSelectIndex !== consts.ChartPeriodTypes.月 || i % 2 === 0
              );
            }),
        },
        yAxis: {
          ...prev.yAxis,
          tickPositions: calculatedYAxis.ticks,
          max: calculatedYAxis.max,
          min: calculatedYAxis.min,
        },
        series,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetList]);

  const drawIcons = ({
    chart,
  }: {
    chart: Highcharts.Chart;
    icons: IconOnChart[];
  }): void => {
    const iconSize = 15;
    const margin = 5;
    icons.forEach(({ icon }) => {
      if (icon.renderer) icon.destroy();
    });
    const xAxis = chart.xAxis[0];

    const incorrect = extraData?.find(it => it.type === 'IncorrectSign');
    if (!incorrect) return;
    const signSeries = incorrect.value
      .filter(it => it.date && it.value)
      .map(it => it.date);

    signSeries.forEach(date => {
      const x = xAxis.toPixels(date.getTime(), true) - 0.5 * iconSize;
      if (!chart.isInsidePlot(x, chart.plotTop)) return;
      const icon = chart.renderer
        .image(
          '/static/images/icon/icon-info.svg',
          x,
          chart.plotTop + chart.plotHeight - 7,
          iconSize,
          iconSize
        )
        .add();

      const tooltipWidth = 150;
      let tx = x - tooltipWidth - margin;
      if (!chart.isInsidePlot(tx, chart.plotTop)) {
        tx = x + iconSize + margin;
      }

      icon.element.addEventListener('mouseover', () => {
        setToolTipPosition({
          x: tx,
          y: chart.chartHeight - chart.plotTop - chart.plotHeight,
          width: tooltipWidth,
          date: date,
        });
        setShowToolTip(true);
      });
      icon.element.addEventListener('mouseout', () => {
        setShowToolTip(false);
      });

      icons.push({ icon });
      setIcons(icons);
    });
  };

  const Tip = ({
    pos,
    show,
  }: {
    pos: { x: number; y: number; width: number; date: Date | null };
    show: boolean;
  }) => {
    return (
      <div
        className="p-2 absolute bg-white rounded-md shadow-md text-xs w-1/2 border border-gray-300"
        style={{
          display: show ? 'block' : 'none',
          zIndex: 999,
          left: pos.x,
          bottom: pos.y,
          maxWidth: pos.width,
        }}>
        <div className="flex flex-row">
          <div
            className="icon-info text-sm"
            id="icon-info"
            style={{ color: '#94A3B8' }}></div>
          <div className="ml-1 text-1.5xs">
            <div>
              集計が正常に完了しなかった可能性があるためこのデータは参考値です。
            </div>
            <div className="mt-1">
              {pos.date !== null
                ? `${pos.date.getFullYear()}/${pos.date.getMonth() +
                    1}/${pos.date.getDate()}`
                : '----/--/--'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <div className="relative">
      <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
      <Tip pos={toolTipPosition} show={showToolTip} />
    </div>
  );
};

export default AssetAllocationChart;
